import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Plus, ArrowLeft, Mail, Phone, Smartphone, Pencil, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDeleteDialog } from "@/components/ConfirmDelete";
import { VerkoopList } from "@/components/VerkoopList";
import PrijslijstTab from "@/pages/PrijslijstTab";
import { KlantDashboard } from "@/components/KlantDashboard";
import { ProductTrends } from "@/components/ProductTrends";
import { KenmerkenPanel } from "@/components/KenmerkenPanel";
import { OrgCampagnesBlok } from "@/components/OrgCampagnesBlok";
import { PresentatieDialog } from "@/components/PresentatieDialog";
import { useAuth } from "@/context/AuthContext";
import { useDetailNav } from "@/lib/overzichtNav";
import { OverzichtNav } from "@/components/OverzichtNav";

function InlineField({ value, onSave, label, type="text", multiline=false }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  const save = async () => { if (v !== value) await onSave(v); setEdit(false); };
  return (
    <div className="group">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {edit ? (
        multiline
          ? <Textarea autoFocus value={v} onChange={(e)=>setV(e.target.value)} onBlur={save} />
          : <Input autoFocus type={type} value={v} onChange={(e)=>setV(e.target.value)} onBlur={save} onKeyDown={(e)=>e.key==="Enter"&&save()} />
      ) : (
        <div onClick={()=>setEdit(true)}
             className="text-sm py-1.5 px-2 -mx-2 rounded hover:bg-secondary cursor-text min-h-[2rem] flex items-center justify-between gap-2">
          <span className={value ? "" : "text-muted-foreground italic"}>{value || "—"}</span>
          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-50" />
        </div>
      )}
    </div>
  );
}

function AdresBlok({ titel, prefix, adres, patchOrg }) {
  const a = adres || {};
  return (
    <div className="space-y-2" data-testid={`adresblok-${prefix}`}>
      <div className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titel}</div>
      <div className="grid grid-cols-2 gap-3">
        <InlineField label="Straat" value={a.straat} onSave={(v)=>patchOrg(`${prefix}.straat`, v)} />
        <InlineField label="Nr." value={a.huisnummer} onSave={(v)=>patchOrg(`${prefix}.huisnummer`, v)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <InlineField label="Postcode" value={a.postcode} onSave={(v)=>patchOrg(`${prefix}.postcode`, v)} />
        <InlineField label="Plaats" value={a.plaats} onSave={(v)=>patchOrg(`${prefix}.plaats`, v)} />
      </div>
      <InlineField label="Land" value={a.land} onSave={(v)=>patchOrg(`${prefix}.land`, v)} />
    </div>
  );
}

// Kaart-only, read-only weergave van contactgegevens. Bewerken gebeurt in de
// Contactgegevens-tab. Dit voorkomt per ongeluk wijzigen tijdens quick-look.
function ReadOnlyLine({ label, value }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="text-sm py-1.5 min-h-[2rem] flex items-center">
        <span className={value ? "" : "text-muted-foreground italic"}>{value || "—"}</span>
      </div>
    </div>
  );
}

function ReadOnlyAdres({ titel, adres }) {
  const a = adres || {};
  const regel1 = [a.straat, a.huisnummer].filter(Boolean).join(" ");
  const regel2 = [a.postcode, a.plaats].filter(Boolean).join(" ");
  return (
    <div className="space-y-1">
      <div className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titel}</div>
      <div className="text-sm text-foreground">
        {regel1 || <span className="text-muted-foreground italic">—</span>}
        {regel2 && <><br />{regel2}</>}
        {a.land && <><br /><span className="text-muted-foreground">{a.land}</span></>}
      </div>
    </div>
  );
}


function NewContactDialog({ orgId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ voornaam: "", achternaam: "", functie: "", email: "", telefoon: "", mobiel: "" });
  const submit = async () => {
    if (!f.voornaam.trim() || !f.achternaam.trim()) { toast.error("Voornaam en achternaam verplicht"); return; }
    const { data } = await api.post("/contactpersonen", { ...f, organisatie_id: orgId });
    toast.success("Contactpersoon toegevoegd"); setOpen(false);
    setF({ voornaam: "", achternaam: "", functie: "", email: "", telefoon: "", mobiel: "" });
    onCreated(data);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="add-contact-btn"><Plus className="h-4 w-4 mr-1" />Contactpersoon</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nieuwe contactpersoon</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Voornaam</Label><Input data-testid="contact-voornaam" value={f.voornaam} onChange={(e)=>setF({...f,voornaam:e.target.value})} /></div>
          <div><Label>Achternaam</Label><Input data-testid="contact-achternaam" value={f.achternaam} onChange={(e)=>setF({...f,achternaam:e.target.value})} /></div>
          <div className="col-span-2"><Label>Functie</Label><Input value={f.functie} onChange={(e)=>setF({...f,functie:e.target.value})} /></div>
          <div className="col-span-2"><Label>E-mail</Label><Input type="email" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} /></div>
          <div><Label>Telefoon</Label><Input value={f.telefoon} onChange={(e)=>setF({...f,telefoon:e.target.value})} /></div>
          <div><Label>Mobiel</Label><Input value={f.mobiel} onChange={(e)=>setF({...f,mobiel:e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="contact-submit">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewActivityDialog({ orgId, contacts, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "taak", onderwerp: "", omschrijving: "", contactpersoon_id: "" , status: "open"});
  const submit = async () => {
    if (!f.onderwerp.trim()) { toast.error("Onderwerp is verplicht"); return; }
    const payload = { ...f, organisatie_id: orgId };
    if (!payload.contactpersoon_id) delete payload.contactpersoon_id;
    const { data } = await api.post("/activiteiten", payload);
    toast.success("Activiteit gelogd"); setOpen(false);
    setF({ type: "taak", onderwerp: "", omschrijving: "", contactpersoon_id: "", status: "open" });
    onCreated(data);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-activity-btn"><Plus className="h-4 w-4 mr-1" />Activiteit</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Activiteit loggen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={f.type} onValueChange={(v)=>setF({...f,type:v})}>
                <SelectTrigger data-testid="act-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["mail","telefoon","gesprek","taak","notitie"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v)=>setF({...f,status:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["open","afgerond"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Onderwerp</Label><Input data-testid="act-onderwerp" value={f.onderwerp} onChange={(e)=>setF({...f,onderwerp:e.target.value})} /></div>
          <div><Label>Omschrijving</Label><Textarea value={f.omschrijving} onChange={(e)=>setF({...f,omschrijving:e.target.value})} /></div>
          {contacts.length > 0 && (
            <div><Label>Contactpersoon (optioneel)</Label>
              <Select value={f.contactpersoon_id || "none"} onValueChange={(v)=>setF({...f,contactpersoon_id: v==="none"?"":v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— geen —</SelectItem>
                  {contacts.map(c=><SelectItem key={c.id} value={c.id}>{c.voornaam} {c.achternaam}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="act-submit">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OrganisatieDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const nav = useDetailNav("organisaties", id, "/organisaties");
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [org, setOrg] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deps, setDeps] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [acts, setActs] = useState([]);
  const [kansen, setKansen] = useState([]);
  const [facturen, setFacturen] = useState([]);
  const [openKans, setOpenKans] = useState(null);
  const [openFact, setOpenFact] = useState(null);
  const [regels, setRegels] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [relVerwijderenAan, setRelVerwijderenAan] = useState(false);

  useEffect(() => {
    api.get("/instellingen/beveiliging").then((r) => setRelVerwijderenAan(!!r.data?.relaties_verwijderen)).catch(() => {});
  }, []);

  const reloadAll = async () => {
    const [o, c, a, k, f] = await Promise.all([
      api.get(`/organisaties/${id}`),
      api.get("/contactpersonen", { params: { organisatie_id: id }}),
      api.get("/activiteiten", { params: { organisatie_id: id }}),
      api.get("/verkoopkansen", { params: { relatie_id: id }}),
      api.get("/verkoopfacturen", { params: { relatie_id: id }}),
    ]);
    setOrg(o.data); setContacts(c.data); setActs(a.data);
    setKansen(k.data); setFacturen(f.data);
    api.get(`/organisaties/${id}/klant-dashboard`).then(({data})=>setDashboard(data)).catch(()=>setDashboard(null));
  };
  useEffect(() => { reloadAll(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { setOrg(null); }, [id]);

  const loadKansRegels = async (kid) => {
    setOpenKans(kid); setOpenFact(null);
    if (!kid) { setRegels([]); return; }
    const { data } = await api.get(`/verkoopkansen/${kid}/regels`);
    setRegels(data);
  };
  const loadFactuurRegels = async (fid) => {
    setOpenFact(fid); setOpenKans(null);
    if (!fid) { setRegels([]); return; }
    const { data } = await api.get(`/verkoopfacturen/${fid}/regels`);
    setRegels(data);
  };

  const patchOrg = async (field, value) => {
    const { data } = await api.patch(`/organisaties/${id}`, { [field]: value });
    setOrg(data); toast.success("Opgeslagen");
  };
  const setArchief = async (val) => {
    await api.patch(`/organisaties/${id}`, { gearchiveerd: val, ...(val ? { actief: false } : { actief: true }) });
    toast.success(val ? "Relatie gearchiveerd" : "Relatie geactiveerd");
    if (val) navigate("/organisaties"); else setOrg({ ...org, gearchiveerd: false });
  };
  const openDelete = async () => {
    try {
      const { data } = await api.get(`/organisaties/${id}/afhankelijkheden`);
      setDeps(data);
    } catch { setDeps(null); }
    setConfirmDel(true);
  };
  const doDelete = async () => {
    try {
      const { data } = await api.delete(`/organisaties/${id}`);
      toast.success(`Relatie verwijderd${data.activiteiten_verwijderd ? ` (${data.activiteiten_verwijderd} activiteiten opgeruimd)` : ""}`);
      navigate("/organisaties");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };
  const toggleAct = async (a) => {
    const newStatus = a.status === "open" ? "afgerond" : "open";
    const { data } = await api.patch(`/activiteiten/${a.id}`, { status: newStatus });
    setActs((x) => x.map(i => i.id === a.id ? data : i));
  };

  if (!org) return <div className="text-muted-foreground">Laden…</div>;

  const fmtEUR = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
  const fmtD = (iso) => { if (!iso) return ""; const d = new Date(iso); if (isNaN(d)) return iso.slice(0,10); const dd=String(d.getDate()).padStart(2,"0"); const mm=String(d.getMonth()+1).padStart(2,"0"); return `${dd}-${mm}-${d.getFullYear()}`; };

  return (
    <div className="space-y-6" data-testid="org-detail">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(nav.terug || "/organisaties")} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Overzicht
        </button>
        <OverzichtNav nav={nav} basePad="/organisaties" testid="org-nav" />
      </div>

      {/* Klant Dashboard bovenaan, volledige breedte */}
      <div data-testid="org-top-dashboard">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <h1 className="font-display text-2xl sm:text-3xl font-medium leading-tight break-words min-w-0 flex-1 sm:flex-none">{org.naam}</h1>
          {org.klantcode && (
            <Badge variant="outline" className="font-mono text-xs" data-testid="org-header-klantcode">{org.klantcode}</Badge>
          )}
          <Badge className="capitalize" variant={org.status==="klant"?"default":org.status==="prospect"?"secondary":"outline"}>{org.status}</Badge>
          {org.gearchiveerd && <Badge variant="outline" className="border-amber-400 text-amber-600">Gearchiveerd</Badge>}
          <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-2 flex-wrap">
            <PresentatieDialog orgId={id} orgNaam={org.naam} />
            {org.gearchiveerd ? (
              <Button variant="outline" size="sm" onClick={() => setArchief(false)} data-testid="org-activeren-btn">
                <ArchiveRestore className="h-4 w-4 mr-1" /> Activeren
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setArchief(true)} data-testid="org-archiveren-btn">
                <Archive className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Archiveren (non-actief)</span><span className="sm:hidden">Archief</span>
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={openDelete} disabled={!relVerwijderenAan}
                title={relVerwijderenAan ? "Relatie verwijderen" : "Verwijderen staat uit — schakel het in via Instellingen → Bedrijf → Beveiliging"}
                className="text-destructive border-destructive/40 hover:bg-destructive/10 disabled:opacity-50" data-testid="org-verwijderen-btn">
                <Trash2 className="h-4 w-4 mr-1" /> Verwijderen
              </Button>
            )}
          </div>
        </div>

        {/* Mobiel: snel-contactbalk bovenaan (bellen / mailen) */}
        {(org.telefoon || org.email) && (
          <div className="lg:hidden mb-4 grid grid-cols-2 gap-2" data-testid="org-mobiel-actiebalk">
            {org.telefoon ? (
              <a href={`tel:${org.telefoon.replace(/\s/g, "")}`} data-testid="org-mobiel-bel"
                className="flex items-center justify-center gap-2 h-12 rounded-lg bg-emerald-500 text-white font-medium active:bg-emerald-600 shadow-sm">
                <Phone className="h-4 w-4" /> Bellen
              </a>
            ) : <div className="flex items-center justify-center gap-2 h-12 rounded-lg bg-secondary text-muted-foreground text-sm">Geen telefoon</div>}
            {org.email ? (
              <a href={`mailto:${org.email}`} data-testid="org-mobiel-mail"
                className="flex items-center justify-center gap-2 h-12 rounded-lg bg-primary text-primary-foreground font-medium active:bg-primary/90 shadow-sm">
                <Mail className="h-4 w-4" /> Mailen
              </a>
            ) : <div className="flex items-center justify-center gap-2 h-12 rounded-lg bg-secondary text-muted-foreground text-sm">Geen e-mail</div>}
          </div>
        )}

        <KlantDashboard data={dashboard} org={org} contacts={contacts} />
      </div>

      <ConfirmDeleteDialog
        open={confirmDel}
        onOpenChange={setConfirmDel}
        onConfirm={doDelete}
        title={`Relatie "${org.naam}" verwijderen?`}
        description={
          deps && deps.activiteiten > 0
            ? `Let op: er ${deps.activiteiten === 1 ? "staat" : "staan"} ${deps.activiteiten} ${deps.activiteiten === 1 ? "activiteit" : "activiteiten"} gekoppeld. Deze worden als failsafe automatisch eerst verwijderd. ${deps.verkoopkansen + deps.verkoopfacturen > 0 ? `Er ${deps.verkoopkansen + deps.verkoopfacturen === 1 ? "is" : "zijn"} ook ${deps.verkoopkansen} verkoopkans(en) en ${deps.verkoopfacturen} factu(u)r(en) — deze blijven bestaan maar verwijzen naar een verwijderde relatie.` : ""} Dit kan niet ongedaan worden gemaakt.`
            : "Dit verwijdert de relatie definitief. Dit kan niet ongedaan worden gemaakt."
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* NAW kaart links */}
        <div className="lg:col-span-1 bg-card border border-border rounded-xl p-4 sm:p-6 space-y-4 h-fit">
          <div>
            <Badge variant="outline" className="capitalize mb-2">{org.type}</Badge>
            <h2 className="font-display text-2xl font-medium leading-tight">{org.naam}</h2>
            <Badge className="mt-2 capitalize" variant={org.status==="klant"?"default":org.status==="prospect"?"secondary":"outline"}>{org.status}</Badge>
          </div>
          <div className="space-y-3 pt-2 border-t border-border">
            <InlineField label="Klantcode" value={org.klantcode} onSave={(v)=>patchOrg("klantcode", (v||"").trim())} testid="org-klantcode" />
            <InlineField label="Klantgroep" value={org.klantgroep} onSave={(v)=>patchOrg("klantgroep", v)} />
            <InlineField label="Branche" value={org.branche} onSave={(v)=>patchOrg("branche", v)} />
            <InlineField label="KvK" value={org.kvk} onSave={(v)=>patchOrg("kvk", v)} />
            <InlineField label="BTW-nummer" value={org.btw_nummer} onSave={(v)=>patchOrg("btw_nummer", v)} />
            <div className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center justify-between">
              <span>Contactgegevens & adres</span>
              <span className="text-[10px] normal-case font-normal text-muted-foreground/80">wijzig via de tab →</span>
            </div>
            <ReadOnlyLine label="Straat + nr." value={[org.straat, org.huisnummer].filter(Boolean).join(" ")} />
            <ReadOnlyLine label="Postcode + plaats" value={[org.postcode, org.plaats].filter(Boolean).join(" ")} />
            <ReadOnlyLine label="Land" value={org.land} />
            <ReadOnlyAdres titel="Bezorgadres" adres={org.verzendadres} />
            <ReadOnlyAdres titel="Factuuradres" adres={org.factuuradres} />
            <div className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contact</div>
            <ReadOnlyLine label="Telefoon" value={org.telefoon} />
            <ReadOnlyLine label="E-mail" value={org.email} />
            <ReadOnlyLine label="Factuur e-mailadres" value={org.factuur_email} />
            <ReadOnlyLine label="Factuurtermijn" value={org.factuurtermijn != null ? `${org.factuurtermijn} dagen` : ""} />
            <ReadOnlyLine label="Website" value={org.website} />
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={org.status} onValueChange={(v)=>patchOrg("status", v)}>
                <SelectTrigger data-testid="org-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["suspect","prospect","klant","inactief"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-3 pt-1 border-t border-border mt-2">
              <div className="min-w-0">
                <Label className="text-xs text-muted-foreground">Tonen op nextbrush.nl</Label>
                <p className="text-[11px] text-muted-foreground/80 leading-tight">Store-locator op de consumentensite</p>
              </div>
              <Switch
                checked={org.show_on_store_locator !== false}
                onCheckedChange={(v)=>patchOrg("show_on_store_locator", v)}
                data-testid="org-store-locator-switch"
              />
            </div>
          </div>
          <KenmerkenPanel orgId={id} />
          <OrgCampagnesBlok orgId={id} />
        </div>

        {/* Tabbladen rechts */}
        <div className="lg:col-span-2 min-w-0">
          <Tabs defaultValue="verkoopkansen">
            <TabsList className="w-full h-auto flex-wrap justify-start sm:justify-center gap-1 p-1">
              <TabsTrigger value="verkoopkansen" data-testid="tab-verkoopkansen">Kansen ({kansen.length})</TabsTrigger>
              <TabsTrigger value="verkoopfacturen" data-testid="tab-verkoopfacturen">Facturen ({facturen.length})</TabsTrigger>
              <TabsTrigger value="contactpersonen" data-testid="tab-contactpersonen">Contactpersonen ({contacts.length})</TabsTrigger>
              <TabsTrigger value="contactgegevens" data-testid="tab-contactgegevens">Contactgegevens</TabsTrigger>
              <TabsTrigger value="activiteiten" data-testid="tab-activiteiten">Activiteiten ({acts.length})</TabsTrigger>
              <TabsTrigger value="prijzen" data-testid="tab-prijzen">Prijzen</TabsTrigger>
              <TabsTrigger value="trends" data-testid="tab-trends">Trends</TabsTrigger>
              <TabsTrigger value="notitie" data-testid="tab-notitie">Notitie</TabsTrigger>
            </TabsList>

            <TabsContent value="contactpersonen" className="space-y-3 mt-4">
              <div className="flex justify-end"><NewContactDialog orgId={id} onCreated={(c)=>setContacts(x=>[c,...x])} /></div>
              <div className="bg-card border border-border rounded-xl divide-y divide-border">
                {contacts.map(c => (
                  <div key={c.id} className="p-4 flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium">
                        {(c.voornaam || c.achternaam) ? `${c.voornaam} ${c.achternaam}`.trim() : (c.functie || c.rol || "Contactpersoon")}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {[c.functie, c.rol, c.afdeling].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div className="text-sm text-right space-y-1">
                      {c.email && <div className="flex items-center justify-end gap-1.5"><Mail className="h-3 w-3" />{c.email}</div>}
                      {c.telefoon && <div className="flex items-center justify-end gap-1.5"><Phone className="h-3 w-3" />{c.telefoon}</div>}
                      {c.mobiel && <div className="flex items-center justify-end gap-1.5 text-muted-foreground"><Smartphone className="h-3 w-3" />{c.mobiel}</div>}
                    </div>
                  </div>
                ))}
                {contacts.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">Nog geen contactpersonen.</div>}
              </div>
            </TabsContent>

            <TabsContent value="activiteiten" className="space-y-3 mt-4">
              <div className="flex justify-end"><NewActivityDialog orgId={id} contacts={contacts} onCreated={(a)=>setActs(x=>[a,...x])} /></div>
              <div className="bg-card border border-border rounded-xl divide-y divide-border">
                {acts.map(a => (
                  <div key={a.id} className="p-4 flex items-start justify-between gap-4" data-testid={`org-act-${a.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">{a.type}</Badge>
                        <span className="font-medium">{a.onderwerp}</span>
                      </div>
                      {a.omschrijving && <div className="text-sm text-muted-foreground mt-1">{a.omschrijving}</div>}
                    </div>
                    <div className="flex items-center gap-3">
                      <Button size="sm" variant={a.status==="open"?"default":"outline"} onClick={()=>toggleAct(a)} className="capitalize">
                        {a.status}
                      </Button>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(a.datum)}</span>
                    </div>
                  </div>
                ))}
                {acts.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">Nog geen activiteiten.</div>}
              </div>
            </TabsContent>

            <TabsContent value="verkoopkansen" className="mt-4">
              <VerkoopList kind="kans" items={kansen} regels={openKans ? regels : []}
                openId={openKans} onToggle={loadKansRegels} onReload={reloadAll}
                relatie_id={id} isAdmin={isAdmin} />
            </TabsContent>

            <TabsContent value="verkoopfacturen" className="mt-4">
              <VerkoopList kind="factuur" items={facturen} regels={openFact ? regels : []}
                openId={openFact} onToggle={loadFactuurRegels} onReload={reloadAll}
                relatie_id={id} isAdmin={isAdmin} />
            </TabsContent>

            <TabsContent value="trends" className="mt-4">
              <ProductTrends orgId={id} />
            </TabsContent>


            <TabsContent value="prijzen" className="mt-4">
              <PrijslijstTab orgId={id} />
            </TabsContent>

            <TabsContent value="notitie" className="mt-4">              <div className="bg-card border border-border rounded-xl p-4">
                <Textarea
                  data-testid="org-notitie"
                  defaultValue={org.notitie}
                  rows={10}
                  placeholder="Schrijf hier vrije notities…"
                  onBlur={(e)=>e.target.value !== org.notitie && patchOrg("notitie", e.target.value)}
                />
                <div className="text-xs text-muted-foreground mt-2">Wordt automatisch opgeslagen bij verlaten van het veld.</div>
              </div>
            </TabsContent>

            <TabsContent value="contactgegevens" className="mt-4 space-y-4">
              <div className="bg-card border border-border rounded-xl p-4 sm:p-6 space-y-3" data-testid="tab-contactgegevens-form">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Bedrijfsgegevens</div>
                <InlineField label="Naam" value={org.naam} onSave={(v)=>{ const s=(v||"").trim(); if(!s){ toast.error("Naam mag niet leeg zijn"); return; } patchOrg("naam", s); }} />
                <InlineField label="Klantgroep" value={org.klantgroep} onSave={(v)=>patchOrg("klantgroep", v)} />
                <InlineField label="Branche" value={org.branche} onSave={(v)=>patchOrg("branche", v)} />
                <div className="grid grid-cols-2 gap-3">
                  <InlineField label="KvK" value={org.kvk} onSave={(v)=>patchOrg("kvk", v)} />
                  <InlineField label="BTW-nummer" value={org.btw_nummer} onSave={(v)=>patchOrg("btw_nummer", v)} />
                </div>

                <div className="pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Kantooradres (hoofdadres)</div>
                <div className="grid grid-cols-2 gap-3">
                  <InlineField label="Straat" value={org.straat} onSave={(v)=>patchOrg("straat", v)} />
                  <InlineField label="Nr." value={org.huisnummer} onSave={(v)=>patchOrg("huisnummer", v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <InlineField label="Postcode" value={org.postcode} onSave={(v)=>patchOrg("postcode", v)} />
                  <InlineField label="Plaats" value={org.plaats} onSave={(v)=>patchOrg("plaats", v)} />
                </div>
                <InlineField label="Land" value={org.land} onSave={(v)=>patchOrg("land", v)} />

                <AdresBlok titel="Bezorgadres" prefix="verzendadres" adres={org.verzendadres} patchOrg={patchOrg} />
                <AdresBlok titel="Factuuradres" prefix="factuuradres" adres={org.factuuradres} patchOrg={patchOrg} />

                <div className="pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contact</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InlineField label="Telefoon" value={org.telefoon} onSave={(v)=>patchOrg("telefoon", v)} />
                  <InlineField label="Website" value={org.website} onSave={(v)=>patchOrg("website", v)} />
                </div>
                <InlineField label="E-mail" value={org.email} type="email" onSave={(v)=>patchOrg("email", v)} />
                <InlineField label="Factuur e-mailadres" value={org.factuur_email} type="email" onSave={(v)=>patchOrg("factuur_email", v)} />
                <InlineField label="Factuurtermijn (dagen)" value={org.factuurtermijn ?? 30} type="number" onSave={(v)=>patchOrg("factuurtermijn", parseInt(v) || 0)} />
              </div>
              <p className="text-xs text-muted-foreground">Wijzigingen worden direct opgeslagen. De kaart links toont deze gegevens alleen ter referentie (niet meer bewerkbaar).</p>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
